import { z } from "zod";
import { SuccessMetaSchema } from "../common";
import { OtpChannelEnum } from "./otp.schema";
import { withDefaults } from "../../utils/validation.helpers";

/**
 * Precios por canal para cada país. El backend lo usa para calcular el costo de cada
 * verificación y mostrar los estimados en el admin en cada paquete.
 *
 * Cubre los mismos países que `COUNTRY_DIAL_CODES`
 * (`constants/phone-country-codes.ts`) — mantener ambas listas sincronizadas.
 *
 * NOTA: estos valores son estimaciones aproximadas de las tarifas públicas de
 * Twilio por país (mensajería SMS y conversaciones de WhatsApp Business), no
 * están confirmadas contra la cuenta de Twilio real del negocio. Validar y
 * ajustar antes de usarlas para facturación en producción.
 */
export const COUNTRY_OTP_PRICING: Record<
  string,
  { whatsapp: number; sms: number }
> = {
  AR: { whatsapp: 0.05, sms: 0.07 },
  CL: { whatsapp: 0.05, sms: 0.035 },
  CO: { whatsapp: 0.05, sms: 0.04 },
  EC: { whatsapp: 0.05, sms: 0.04 },
  ES: { whatsapp: 0.05, sms: 0.045 },
  GT: { whatsapp: 0.05, sms: 0.04 },
  MX: { whatsapp: 0.05, sms: 0.035 },
  PA: { whatsapp: 0.05, sms: 0.04 },
  PE: { whatsapp: 0.05, sms: 0.04 },
  PY: { whatsapp: 0.05, sms: 0.04 },
  US: { whatsapp: 0.005, sms: 0.0079 },
  VE: { whatsapp: 0.05, sms: 0.04 },
  DEFAULT: { whatsapp: 0.05, sms: 0.1 },
};

export const OTP_TOPUP_PACKAGES = [
  { id: "starter", name: "Starter Pack", price_usd: 5 },
  { id: "basic", name: "Basic Pack", price_usd: 10 },
  { id: "growth", name: "Growth Pack", price_usd: 20 },
  { id: "pro", name: "Pro Pack", price_usd: 50 },
  { id: "scale", name: "Scale Pack", price_usd: 100 },
  { id: "business", name: "Business Pack", price_usd: 250 },
  { id: "enterprise", name: "Enterprise", price_usd: 500 },
] as const;

export type OtpTopUpPackageId = (typeof OTP_TOPUP_PACKAGES)[number]["id"];

export const OtpTopUpPackageIdSchema = z.enum(
  OTP_TOPUP_PACKAGES.map((p) => p.id) as [
    OtpTopUpPackageId,
    ...OtpTopUpPackageId[],
  ]
);

// ========
// UI TEXTS
// ========

/**
 * Shape sin `.default()` — sirve de base para `UpdateOtpConfigRequestSchema.ui_texts`,
 * de modo que las keys no enviadas se parseen como `undefined` (en Zod v4,
 * `.partial()` sobre un campo con `.default()` devuelve el default, no `undefined`).
 */
export const OtpUiTextsBaseSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  verify_button: z.string().min(1),
  resend_button: z.string().min(1),
  change_phone_button: z.string().min(1),
  hint_text: z.string().min(1),
  new_code_sent_text: z.string().min(1),
  invalid_code_error: z.string().min(1),
  resends_limit_error: z.string().min(1),
  contact_support_button: z.string(),
  support_message_template: z.string(),
});

/**
 * Todos los strings que el merchant puede personalizar en el widget OTP.
 * Los campos que aceptan {phone_number} lo reemplaza el frontend en runtime.
 */
export const OtpUiTextsSchema = withDefaults(OtpUiTextsBaseSchema, {
  title: "Verify your phone number to complete your order",
  /** Acepta {phone_number} como variable de interpolación. */
  description:
    "A verification code was sent via SMS/WhatsApp to your phone number {phone_number}. Enter your code below to verify your phone number:",
  verify_button: "Verify",
  resend_button: "Resend code",
  change_phone_button: "Change phone number",
  hint_text: "Didn't receive your code?",
  /** Acepta {phone_number} como variable de interpolación. */
  new_code_sent_text:
    "A new code was sent via SMS to your phone number {phone_number}. Enter the code below:",
  invalid_code_error: "The code you entered is not valid, please try again.",
  resends_limit_error:
    "You have reached the limit of resends, please try again with a different phone number.",
  contact_support_button: "Contact Support",
  support_message_template:
    "Hi, I'm trying to complete my order but I haven't received my verification code. Can you help me?",
});

// ============
// SMS TEMPLATE
// ============

const OTP_SMS_TEMPLATE_MIN_LENGTH = 10;
const OTP_SMS_TEMPLATE_MAX_LENGTH = 140;
const OTP_SMS_TEMPLATE_PLACEHOLDER = "{otp_code}";
export const OTP_SMS_TEMPLATE_DEFAULT = "Your verification code is {otp_code}.";

/**
 * Cuerpo literal del SMS enviado vía Twilio (distinto de `ui_texts`, que es el copy del
 * widget). Solo se usa para el canal `sms` y la pata SMS de `whatsapp_fallback_sms` — el
 * mensaje de WhatsApp no es personalizable (requiere plantilla aprobada por Meta).
 */
export const OtpSmsTemplateSchema = z
  .string()
  .min(
    OTP_SMS_TEMPLATE_MIN_LENGTH,
    `El mensaje debe tener al menos ${OTP_SMS_TEMPLATE_MIN_LENGTH} caracteres`
  )
  .max(
    OTP_SMS_TEMPLATE_MAX_LENGTH,
    `El mensaje no puede superar los ${OTP_SMS_TEMPLATE_MAX_LENGTH} caracteres`
  )
  .refine((value) => value.includes(OTP_SMS_TEMPLATE_PLACEHOLDER), {
    message: `El mensaje debe incluir el marcador ${OTP_SMS_TEMPLATE_PLACEHOLDER}`,
  })
  // Best-effort guard against the obvious case (explicit http(s):// or www.) —
  // not a guarantee: bare domains (mitienda.com) or shorteners (bit.ly/x)
  // without a protocol/www prefix won't be caught.
  .refine((value) => !/https?:\/\/|www\./i.test(value), {
    message: "El mensaje no puede contener enlaces",
  });

// =========================================================================
// OTP CONFIG
// =========================================================================

export const OtpStrategyEnum = z.enum([
  "always", // Siempre pide OTP antes de finalizar
  "high_risk_only", // Solo si el motor de fraude marca riesgo
  "order_value_high", // Solo si el valor del pedido supera un umbral (e.g. $100)
  // (Futuro) Si el canal es whatsapp_silent, el frontend no muestra los 6 cuadritos,
  // sino un solo botón que abre un Deep Link:
  // `https://wa.me/NUMERO?text=Verify%20Order%20${request_id}`
  "whatsapp_silent",
  "disabled", // Nunca pide OTP
]);

const OtpSecurityRulesBaseSchema = z.object({
  /** Rechaza números que no sean líneas móviles reales (bloquea VoIP/Landlines) */
  block_voip_numbers: z.boolean(),

  /** Solo permite números que coincidan con el país de destino (Shipping Address) */
  require_country_match: z.boolean(),
});

const OtpSecurityRulesSchema = withDefaults(OtpSecurityRulesBaseSchema, {
  block_voip_numbers: true,
  require_country_match: true,
});

/**
 * Shape sin `.default()` — sirve de base para `UpdateOtpConfigRequestSchema`, de modo
 * que las keys no enviadas se parseen como `undefined` (en Zod v4, `.partial()` sobre
 * un campo con `.default()` devuelve el default, no `undefined`).
 */
export const OtpConfigBaseSchema = z.object({
  is_enabled: z.boolean(),
  channel: OtpChannelEnum,
  strategy: OtpStrategyEnum,
  /** Solo se aplica si strategy === "order_value_high" */
  min_order_value_for_otp: z.number().nonnegative().optional(),

  /**
   * Cuántas horas se considera válida una verificación previa para el mismo número.
   * Si el cliente vuelve dentro de este periodo, se salta el OTP.
   * Rango: 1h – 720h. Default: 24h.
   */
  validity_period_hours: z.number().int().min(1).max(720),

  /**
   * Máximo de reenvíos por request_id antes de bloquear y mostrar
   * `resends_limit_error`.
   */
  max_resends: z.number().int().min(1).max(10),

  /**
   * Máximo de intentos de código incorrecto antes de bloquear ese request_id
   * (`MAX_ATTEMPTS`). Concepto distinto de `max_resends`: este limita cuántas
   * veces se puede *adivinar* un código ya enviado, no cuántos códigos nuevos
   * se pueden pedir.
   */
  max_verify_attempts: z.number().int().min(1).max(10),

  /** Cuerpo del SMS. Ver `OtpSmsTemplateSchema`. */
  sms_template: OtpSmsTemplateSchema,

  security_rules: OtpSecurityRulesBaseSchema,

  ui_texts: OtpUiTextsBaseSchema,
});

export const OtpConfigSchema = withDefaults(OtpConfigBaseSchema, {
  is_enabled: false,
  channel: "whatsapp",
  strategy: "disabled",
  validity_period_hours: 24,
  max_resends: 3,
  max_verify_attempts: 5,
  sms_template: OTP_SMS_TEMPLATE_DEFAULT,
}).extend({
  security_rules: OtpSecurityRulesSchema.default({
    block_voip_numbers: true,
    require_country_match: true,
  }),
  ui_texts: OtpUiTextsSchema,
});

// =========================================================================
// GET /admin/otp-config
// =========================================================================

export const GetOtpConfigResponseSchema = OtpConfigSchema.extend({
  shop_id: z.string(),
  updated_at: z.iso.datetime(),
});

// =========================================================================
// PUT /admin/otp-config
// =========================================================================

/**
 * Partial: el merchant puede actualizar solo los campos que desee.
 * security_rules y ui_texts también son partial para no forzar todas sus keys a la vez.
 * Se deriva de los schemas *Base* (sin `.default()`) para que las keys no enviadas se
 * parseen como `undefined` en lugar de resolver al valor default.
 */
export const UpdateOtpConfigRequestSchema = OtpConfigBaseSchema.omit({
  security_rules: true,
  ui_texts: true,
})
  .partial()
  .extend({
    security_rules: OtpSecurityRulesBaseSchema.partial().optional(),
    ui_texts: OtpUiTextsBaseSchema.partial().optional(),
  });

// =========================================================================
// BALANCE & BILLING
// =========================================================================

/**
 * Contactos para alertas de saldo bajo.
 * Un merchant puede tener un email + un WhatsApp (ver "Add your WhatsApp").
 */
export const NotificationContactSchema = z.object({
  type: z.enum(["email", "whatsapp"]),
  value: z.string().min(1),
});

export const OtpBalanceSchema = z.object({
  /** Saldo disponible en USD. */
  available_balance_usd: z.number().nonnegative(),
  free_balance_usd: z.number().nonnegative().default(0),

  /**
   * Umbral en USD a partir del cual se dispara la alerta de saldo bajo.
   * El backend lo determina internamente, se expone para mostrarlo en el admin.
   */
  low_balance_threshold_usd: z.number().nonnegative(),

  notification_contacts: z.array(NotificationContactSchema),
});

// =========================================================================
// GET /admin/otp-balance
// =========================================================================

export const GetOtpBalanceResponseSchema = OtpBalanceSchema.extend({
  shop_id: z.string(),
  verifications_sent: z.number().int().nonnegative(),
  /** Precios por canal para el país del merchant, solo lectura. */
  pricing: z.object({
    sms_per_message_usd: z.number().nonnegative(),
    whatsapp_per_message_usd: z.number().nonnegative(),
    country_code: z.string().length(2),
  }),
  recent_purchases: z.array(
    z.object({
      id: z.string(),
      package_id: OtpTopUpPackageIdSchema,
      price_usd: z.number(),
      /**
       * Snapshot calculado al momento de la compra con la tarifa vigente del merchant.
       * No se recalcula si cambia el canal o país después.
       */
      est_verifications_whatsapp: z.number().int(),
      est_verifications_sms: z.number().int(),
      status: z.enum(["PENDING", "CONFIRMED", "DECLINED", "EXPIRED"]),
      created_at: z.iso.datetime(),
      confirmed_at: z.iso.datetime().nullable(),
    })
  ),
  packages: z.array(
    z.object({
      id: OtpTopUpPackageIdSchema,
      name: z.string(),
      price_usd: z.number(),
      // Calculado por el backend según pricing.{channel}_per_message_usd del merchant
      est_verifications_whatsapp: z.number().int(),
      est_verifications_sms: z.number().int(),
    })
  ),
});

// =========================================================================
// POST /admin/otp-balance/topup
// =========================================================================

export const TopUpRequestSchema = z.object({
  package_id: OtpTopUpPackageIdSchema,
  return_url: z.url(),
});

export const TopUpResponseSchema = z.object({
  meta: SuccessMetaSchema,
  /**
   * URL de shopify billing.
   * El frontend redirige al merchant aquí para completar el pago.
   */
  confirmation_url: z.url(),
  purchase_id: z.string(),
});

// =========================================================================
// PUT /admin/otp-balance/notification-contacts
// =========================================================================

export const UpdateNotificationContactsRequestSchema = z.object({
  notification_contacts: z
    .array(NotificationContactSchema)
    .min(1)
    .max(2) // 1 email + 1 WhatsApp máximo
    .refine(
      (contacts) => {
        const types = contacts.map((c) => c.type);
        return new Set(types).size === types.length;
      },
      { message: "Solo se permite un contacto por tipo (email, whatsapp)" }
    ),
});

export const UpdateNotificationContactsResponseSchema = z.object({
  meta: SuccessMetaSchema,
  notification_contacts: z.array(NotificationContactSchema),
});

// =========================================================================
// TYPE EXPORTS
// =========================================================================

export type OtpUiTexts = z.infer<typeof OtpUiTextsSchema>;
export type OtpStrategy = z.infer<typeof OtpStrategyEnum>;
export type OtpConfig = z.infer<typeof OtpConfigSchema>;
export type GetOtpConfigResponse = z.infer<typeof GetOtpConfigResponseSchema>;
export type UpdateOtpConfigRequest = z.infer<
  typeof UpdateOtpConfigRequestSchema
>;
export type NotificationContact = z.infer<typeof NotificationContactSchema>;
export type GetOtpBalanceResponse = z.infer<typeof GetOtpBalanceResponseSchema>;
export type TopUpRequest = z.infer<typeof TopUpRequestSchema>;
export type TopUpResponse = z.infer<typeof TopUpResponseSchema>;
export type UpdateNotificationContactsRequest = z.infer<
  typeof UpdateNotificationContactsRequestSchema
>;
export type UpdateNotificationContactsResponse = z.infer<
  typeof UpdateNotificationContactsResponseSchema
>;
